/**
 * Animated RoniDoc.ai logo.
 *
 * Renders the brand wordmark as live text (Montserrat 800) so it stays
 * crisp at any size, and animates 3 dots that fall in above the R with
 * a slight bounce - they land in umlaut position each time the
 * component mounts.
 *
 * Sizing: the parent picks the size via `style={{ fontSize: 24 }}` (or
 * a Tailwind text-* class). The container hugs the text, and the dots
 * are positioned in % of the container so they scale with whatever
 * size you choose.
 *
 * Tunable knobs:
 *   - DOT_POSITIONS:  left/top % of each dot
 *   - DOT_SIZE:       diameter as % of logo width
 *   - DOT_COLOR:      blue ink matching the "Roni" word
 *   - STAGGER_MS:     gap between dot animations
 */

import { CSSProperties, useEffect } from 'react';

const BRAND_BLUE = '#2C45AB';
const DOT_COLOR  = BRAND_BLUE;

// Horizontal positions are % of the rendered logo width - they scale
// with the text. Vertical position is in em so the dots sit a known
// distance above the R cap-height regardless of font-size. Montserrat
// 800 has cap-height ~0.72em, so the R's top is roughly 0.28em below
// the em-box top, which puts it ~0.83em below the container top
// (paddingTop 0.55em + 0.28em). Place the dots just above that.
const DOT_POSITIONS: Array<{ left: string; top: string }> = [
  { left: '0%', top: 'calc(0.5em - 1px)' },   // leftmost dot aligns with R's left edge; net 1px above 0.5em
  { left: '4%', top: 'calc(0.5em - 1px)' },
  { left: '8%', top: 'calc(0.5em - 1px)' },
];
const DOT_SIZE = '2.88%';                     // 80% of the previous 3.6%
const STAGGER_MS = 120;
const FALL_DURATION_MS = 600;

const FONT_LINK_ID = 'ronidoc-montserrat-font';

interface Props {
  /** Override container styles (font-size, width, color, etc.). */
  style?: CSSProperties;
  className?: string;
  /** Optional aria label. */
  alt?: string;
}

export function AnimatedRoniDocLogo({ className, style, alt = 'RoniDoc.ai' }: Props) {
  // Lazy-inject the Montserrat font once. Safe to call multiple times;
  // every subsequent mount just no-ops because the <link> exists.
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800;900&display=swap';
    document.head.appendChild(link);
  }, []);

  return (
    <span
      role="img"
      aria-label={alt}
      className={className}
      style={{
        position: 'relative',
        display: 'inline-block',
        fontFamily: '"Montserrat", system-ui, sans-serif',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        // Leave headroom for the dots above the R. Padding scales with
        // font-size (1em) so it stays proportional at any size.
        paddingTop: '0.55em',
        ...style,
      }}
    >
      <span style={{ color: BRAND_BLUE }}>Roni</span>
      <span style={{ color: 'currentColor' }}>Doc.ai</span>

      {DOT_POSITIONS.map((p, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            position: 'absolute',
            left: p.left,
            top: p.top,
            width: DOT_SIZE,
            paddingBottom: DOT_SIZE,
            height: 0,
            borderRadius: '50%',
            backgroundColor: DOT_COLOR,
            transform: 'translateY(-220%)',
            opacity: 0,
            animation: `ronidoc-dot-in ${FALL_DURATION_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) ${i * STAGGER_MS}ms forwards`,
          }}
        />
      ))}

      <style>{`
        @keyframes ronidoc-dot-in {
          0%   { transform: translateY(-220%); opacity: 0; }
          60%  { transform: translateY(12%);   opacity: 1; }
          100% { transform: translateY(0);     opacity: 1; }
        }
      `}</style>
    </span>
  );
}

export default AnimatedRoniDocLogo;
